import { ReactNode, useContext } from 'react';

import { Path, StatusLabel } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { NoPage } from '@shared/pages';
import { SignInContext } from '@modules/users';
import { FoldersNavDrawer, isTFolder, TFolder, TPath, useFolder } from '@modules/items';

import { TRAINING_RECORDS_PATH } from '../constants';

type TProps = {
  children: ReactNode;
};

type TInnerProps = TProps & {
  qmFolder: TFolder;
};

// Training records are company-wide and live under QM in the nav, so the drawer and
// breadcrumbs are driven by the QM folder - mirroring the changeV2 scaffold.
const TrainingScaffoldInner = ({ qmFolder, children }: TInnerProps) => {
  const fullPath: TPath = [
    ...qmFolder.resolvedPath,
    { _id: qmFolder._id, name: qmFolder.name, accessRight: qmFolder.accessRight },
    { _id: 'training', name: 'Training', accessRight: qmFolder.accessRight, href: TRAINING_RECORDS_PATH },
  ];

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={qmFolder} fullPath={fullPath} selectedValue="training" />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8 max-w-screen-2xl">
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrainingPageScaffold = ({ children }: TProps) => {
  const { organization } = useContext(SignInContext);
  const qmFolderId = organization.folders.qm;

  const { data, error } = useFolder(qmFolderId, { enabled: Boolean(qmFolderId) });

  if (!qmFolderId) return <NoPage />;

  return (
    <StatusLabel
      data={data}
      error={error}
      loadingMessage="Loading training..."
      errorMessage="Loading training failed!"
      render={(folder) =>
        isTFolder(folder) ? (
          <TrainingScaffoldInner qmFolder={folder as TFolder}>{children}</TrainingScaffoldInner>
        ) : (
          <NoPage />
        )
      }
    />
  );
};

export default TrainingPageScaffold;
